import React, { useState } from 'react';
import { strategyCardId } from '../utils/limitStatus';
import { Box, Card, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse, IconButton, Tooltip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, KeyboardArrowDown, KeyboardArrowUp, ShowChart as ShowChartIcon, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import StrategyTypeDrillDownModal from '../../risk-limits/components/StrategyTypeDrillDownModal';
import { varLimitStatus, varLimitUsedPct, exposureLimitUsed } from '../utils/limitStatus';

const VAR_STATUS_STYLE = {
  good: { bg: '#f8fafc', border: '#e2e8f0', color: '#059669' },
  warning: { bg: '#fffbeb', border: '#fde68a', color: '#d97706' },
  critical: { bg: '#fef2f2', border: '#fecaca', color: '#dc2626' },
};

const LIMIT_STATUS_LABEL = { good: 'Within Limit', warning: 'Near Limit', critical: 'Over Limit' };

const getStrategyColor = (strategyName) => {
  const colorMap = {
    'MERGER ARBITRAGE': '#667eea',
    'EDFI': '#10b981',
    'SPAC LIQUIDATION ARBITRAGE': '#f59e0b',
    'CONVERTIBLE ARBITRAGE': '#3b82f6',
    'CREDIT ARBITRAGE': '#8b5cf6',
    'VOL ARBITRAGE': '#ec4899',
    'STAT ARBITRAGE': '#06b6d4',
  };
  return colorMap[strategyName] || '#ffffff';
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatInMillions = (value) => {
  if (value === null || value === undefined || value === 0) return '-';
  const millions = value / 1_000_000;
  // 1-digit millions: 2 decimals, 2-digit: 1 decimal, 3+ digits (and sub-1M): see below.
  const abs = Math.abs(millions);
  const decimals = abs < 10 ? 2 : abs < 100 ? 1 : 0;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(millions);
  return `${formatted}M`;
};

const RISK_MONITOR_STRATEGY_NAMES = { EDFI: 'EVENT DRIVEN FIXED INCOME' };

// Millions, except magnitudes under 100k which show in thousands.
const formatInMillionsOrThousands = (value) => {
  if (value === null || value === undefined || value === 0) return '-';
  if (Math.abs(value) >= 100_000) return formatInMillions(value);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value / 1_000);
  return `${formatted}K`;
};

const ValueWithTooltip = ({ value, label = '', children, kUnderHundredK = false }) => {
  if (!value && value !== 0) {
    return <span>-</span>;
  }
  const isNegative = value < 0;
  const wrap = (text) => (isNegative ? `(${text})` : text);
  const fullValue = wrap(formatCurrency(Math.abs(value)));
  const displayValue = wrap(
    (kUnderHundredK ? formatInMillionsOrThousands : formatInMillions)(Math.abs(value))
  );

  return (
    <Tooltip title={`${label ? label + ': ' : ''}${fullValue}`} arrow>
      <span style={{ cursor: 'help', borderBottom: '1px dotted #999', ...(isNegative ? { color: '#c53030' } : {}) }}>
        {children || displayValue}
      </span>
    </Tooltip>
  );
};

const MetricCard = ({ label, value, format = 'currency', color }) => (
  <Box sx={{ flex: 1, minWidth: '100px' }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontSize: '11px',
        fontWeight: '600',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '14px',
        fontWeight: '600',
        color: color || '#1e293b',
      }}
    >
      {format === 'currency' ? formatCurrency(value) : String(value)}
    </Typography>
  </Box>
);

// Collapsible table section with expand/collapse button
const CollapsibleTable = ({ title, data = [], aum = 0, riskLabel = 'Risk', limit = 5 }) => {
  const [expanded, setExpanded] = useState(false);

  const rows = Array.isArray(data) ? data : [];

  const isSpacData = title.toLowerCase().includes('spac');
  const isDealBreakRisk = title.toLowerCase().includes('deal');

  // No data for this section (for example no day loaded yet): hide the whole section, header included.
  if (rows.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Header with expand button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: '12px',
            fontWeight: '700',
            color: '#1e293b',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            flex: 1,
          }}
        >
          {title}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
          }}
        >
          <ExpandMoreIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      </Box>

      {/* Collapsible table */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <TableContainer sx={{ maxHeight: '200px' }}>
          <Table
            size="small"
            stickyHeader
            sx={{
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              '& .MuiTableHead-root .MuiTableCell-root': { backgroundColor: '#f1f5f9' },
            }}
          >
            <TableHead>
              <TableRow>
                {isSpacData ? (
                  <>
                    <TableCell sx={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>
                      Symbol
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>
                      Current Price
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>
                      LMV
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>
                      {riskLabel}
                    </TableCell>
                  </>
                ) : isDealBreakRisk ? (
                  <>
                    <TableCell sx={{ fontSize: '11px', fontWeight: '600', color: '#64748b', width: '150px' }}>
                      Strategy
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>
                      Deal Break Risk
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontSize: '11px', fontWeight: '600', color: '#64748b', width: '150px' }}>
                      Strategy
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>
                      GMV
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, limit).map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                    },
                  }}
                >
                  {isSpacData ? (
                    <>
                      <TableCell sx={{ fontSize: '11px', color: '#1e293b' }}>
                        {row.symbol}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '11px', color: '#1e293b' }}>
                        {Number.isFinite(row.current_price) ? row.current_price.toFixed(2) : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '11px', color: '#1e293b' }}>
                        {formatCurrency(row.lmv)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '11px', color: '#B85450', fontWeight: '500' }}>
                        {formatCurrency(row.risk)}
                      </TableCell>
                    </>
                  ) : isDealBreakRisk ? (
                    <>
                      <TableCell sx={{ fontSize: '11px', color: '#1e293b', width: '150px' }}>
                        {row.strategy_name}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '11px', color: '#B85450', fontWeight: '500' }}>
                        {formatCurrency(row.dealBreakRisk)}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ fontSize: '11px', color: '#1e293b', width: '150px' }}>
                        {row.strategy_name}
                        {row.position_count != null && (
                          <Typography component="div" sx={{ fontSize: '11px', color: '#64748b' }}>
                            {row.position_count} {row.position_count === 1 ? 'position' : 'positions'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '11px', color: '#1e293b' }}>
                        {formatCurrency(row.gmv)}
                        <Typography component="div" sx={{ fontSize: '11px', color: '#64748b' }}>
                          {aum > 0 && Number.isFinite(Number(row.gmv)) ? `${((Number(row.gmv) / aum) * 100).toFixed(2)}% of AUM` : '- of AUM'}
                        </Typography>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
};

export default function StrategyBubble({ strategy, asOfDate, viewMode = 'threshold', drivingMeasure = 'GMV', displayMode = 'millions', aum = 0, portfolioGreeks = null, onSelectTrend, density = 'comfortable' }) {
  const compact = density === 'compact';
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [displayInAum, setDisplayInAum] = useState(false);
  const color = getStrategyColor(strategy.strategy_type_name);
  const isMergerArb = strategy.strategy_type_name === 'MERGER ARBITRAGE';
  const isSpac = strategy.strategy_type_name === 'SPAC LIQUIDATION ARBITRAGE';

  // Risk Monitor keys by_strategy by its own strategy names (EDFI bucket is one type).
  const greeksKey = RISK_MONITOR_STRATEGY_NAMES[strategy.strategy_type_name] || strategy.strategy_type_name;
  // This strategy's P&L per shock scenario; skip the no-shock Base row.
  const strategyGreeks = (portfolioGreeks?.scenarios || [])
    .map((s) => ({ ...s, pnl: s.by_strategy?.[greeksKey]?.pnl }))
    // Only scenarios available for this strategy (present and non-zero) are shown.
    .filter((s) => s.sim_number !== 0 && s.pnl != null && s.pnl !== 0)
    .sort((a, b) => a.sim_number - b.sim_number);
  // Portfolio Greeks are not shown for SPAC liquidation arbitrage.
  const hasStrategyGreeks = !isSpac && strategyGreeks.length > 0;

  const getDisplayValue = (rawValue, label) => {
    // SMV is reported as a positive magnitude but is a short (negative) exposure.
    const value = label === 'SMV' ? -Math.abs(rawValue) : rawValue;
    if (displayMode === 'percentage' && aum && aum > 0) {
      const percentage = (value / aum) * 100;
      const text = `${Math.abs(percentage).toFixed(1)}%`;
      return percentage < 0 ? <span style={{ color: '#c53030' }}>({text})</span> : text;
    }
    return (
      <ValueWithTooltip
        value={value}
        label={label}
      />
    );
  };

  const measureKey = drivingMeasure.toLowerCase();

  // When displayInAum is true, calculate utilization based on exposure/AUM instead of exposure/RC
  const getUtilizationValue = (exposure, measureType) => {
    if (displayInAum && aum && aum > 0) {
      return (exposure / aum) * 100;
    }
    if (measureType === 'nmv') {
      return strategy.risk_capital ? (Math.abs(exposure) / strategy.risk_capital) * 100 : 0;
    }
    return strategy.risk_capital ? (exposure / strategy.risk_capital) * 100 : 0;
  };

  const ratios = {
    gmv: {
      value: getUtilizationValue(strategy.gmv, 'gmv'),
      max: strategy.max_gmv_pct || 100,
      label: displayInAum ? 'GMV/AUM' : 'GMV/RC',
      rawValue: strategy.gmv,
    },
    lmv: {
      value: getUtilizationValue(strategy.lmv, 'lmv'),
      max: strategy.max_lmv_pct || 100,
      label: displayInAum ? 'LMV/AUM' : 'LMV/RC',
      rawValue: strategy.lmv,
    },
    smv: {
      value: getUtilizationValue(strategy.smv, 'smv'),
      max: strategy.max_smv_pct || 100,
      label: displayInAum ? 'SMV/AUM' : 'SMV/RC',
      rawValue: strategy.smv,
    },
    nmv: {
      value: getUtilizationValue(strategy.nmv, 'nmv'),
      max: strategy.max_nmv_pct || 100,
      label: displayInAum ? 'NMV/AUM' : 'NMV/RC',
      rawValue: strategy.nmv,
    },
  };

  const currentRatio = ratios[measureKey] || ratios.gmv;
  const utilizationPercent = currentRatio.value;
  // % of the driving measure's own limit used, on the same good/warning(95-100%)/critical(>100%) standard as the
  // per-exposure temperature line below, so the card border, bar and badge agree with it.
  const drivingUsedPct = currentRatio.max ? (utilizationPercent / currentRatio.max) * 100 : null;
  const drivingStatus = varLimitStatus(drivingUsedPct);
  const drivingStatusStyle = VAR_STATUS_STYLE[drivingStatus] || VAR_STATUS_STYLE.good;
  const getUtilizationColor = () => drivingStatusStyle.color;
  // The bar follows the ratio's status: green is unchanged; amber and red get a
  // stronger fill (amber shading toward orange-red; a darker red) plus a light tint of the same hue on the track.
  const getUtilizationBarColors = () => {
    if (drivingStatus === 'critical') return { fill: '#991b1b', track: '#fecaca' };
    if (drivingStatus === 'warning') return { fill: 'linear-gradient(90deg, #e8890a 0%, #d9541a 100%)', track: '#fde7c3' };
    return { fill: '#059669', track: '#e2e8f0' };
  };
  const utilizationBar = getUtilizationBarColors();
  // Same three states as the bar: within limit, near limit, over limit.
  const getUtilizationTint = () => ({ bg: drivingStatusStyle.bg, border: drivingStatusStyle.border });
  const utilizationTint = getUtilizationTint();
  const drivingLabel = measureKey.toUpperCase();
  const drivingColor = measureKey === 'lmv' ? '#006842' : measureKey === 'smv' ? '#B85450' : '#1e293b';
  // Status in words as well as colour.
  const utilizationStatus = {
    label: LIMIT_STATUS_LABEL[drivingStatus] || 'Within Limit',
    color: drivingStatusStyle.color,
    bg: drivingStatusStyle.bg,
  };
  const headroomPts = currentRatio.max - utilizationPercent;
  const headroomText = headroomPts >= 0 ? `${headroomPts.toFixed(1)} pts headroom` : `${Math.abs(headroomPts).toFixed(1)} pts over`;

  // % of each exposure's own risk-capital limit used (e.g. GMV at 350% of RC against a 500% max is 70% of limit).
  const exposureLimits = {
    gmv: exposureLimitUsed(strategy, 'gmv'),
    lmv: exposureLimitUsed(strategy, 'lmv'),
    smv: exposureLimitUsed(strategy, 'smv'),
    nmv: exposureLimitUsed(strategy, 'nmv'),
  };
  // good/warning/critical, same bands as varLimitStatus (>100% breached, 95-100% approaching).
  const measureStatus = (measure) => {
    const usage = exposureLimits[measure];
    if (!usage || usage.usedPct == null || !Number.isFinite(usage.usedPct)) return null;
    return varLimitStatus(usage.usedPct);
  };
  const labelColor = (measure) => VAR_STATUS_STYLE[measureStatus(measure)]?.color || '#64748b';
  // Thin color-coded bar for the exposure's breach stage, followed by its three figures: raw
  // utilization (exposure/RC), the configured limit, and utilization as a % of that limit.
  const renderTemperatureLine = (measure) => {
    const usage = exposureLimits[measure];
    const status = measureStatus(measure);
    if (!status) return null;
    const style = VAR_STATUS_STYLE[status];
    const stat = (label, value, emphasis) => (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
        <Typography sx={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', lineHeight: 1.45 }}>{label}</Typography>
        <Typography sx={{ fontSize: '11px', fontWeight: '700', color: emphasis ? style.color : '#334155', whiteSpace: 'nowrap', lineHeight: 1.45 }}>{value}</Typography>
      </Box>
    );
    return (
      <Box sx={{ mt: 0.5 }}>
        {/* Bar fills to the share of the max used; the tick marks 100% (the max). Status is also spelled out below. */}
        <Box
          role="progressbar"
          aria-valuenow={Math.round(Math.min(usage.usedPct, 100))}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${usage.measure} ${usage.usedPct.toFixed(0)} percent of max, ${LIMIT_STATUS_LABEL[status]}`}
          sx={{ position: 'relative', height: '5px', borderRadius: '3px', backgroundColor: style.border }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${Math.min(usage.usedPct, 100)}%`,
              borderRadius: '3px',
              backgroundColor: style.color,
            }}
          />
          <Box sx={{ position: 'absolute', right: 0, top: '-2px', width: '2px', height: '9px', backgroundColor: '#94a3b8', borderRadius: '1px' }} />
        </Box>
        <Box sx={{ mt: 0.5 }}>
          {stat('Of RC', `${usage.utilization.toFixed(0)}%`)}
          {stat('Max', `${usage.max.toFixed(0)}%`)}
          {stat('Used', `${usage.usedPct.toFixed(0)}%`, true)}
          <Typography sx={{ fontSize: '11px', fontWeight: '700', color: style.color, lineHeight: 1.45 }}>{LIMIT_STATUS_LABEL[status]}</Typography>
        </Box>
      </Box>
    );
  };

  const strategyNameColor = '#1e293b';

  return (
    <Card
      id={strategyCardId(strategy.strategy_type_name)}
      elevation={0}
      sx={{
        p: compact ? 1.5 : 2.5,
        scrollMarginTop: 80,
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        // Status lives in the left edge only; the card itself stays neutral.
        borderLeft: `4px solid ${getUtilizationColor()}`,
        position: 'relative',
        overflow: 'hidden',
        fontVariantNumeric: 'tabular-nums',
        transition: 'box-shadow 160ms ease',
        '&:hover': { boxShadow: '0 2px 10px rgba(15,23,42,0.08)' },
        // Six rows shared with the neighbouring cards (subgrid), so each block ends on the same
        // line across a row of cards. Spacing between rows of cards comes from the margin.
        display: 'grid',
        gridRow: 'span 6',
        gridTemplateRows: 'subgrid',
        rowGap: 0,
        mb: 2,
      }}
    >
      {/* Strategy Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
        <Typography
          variant="h6"
          title={`${strategy.strategy_type_name} - open details`}
          role="button"
          tabIndex={0}
          onClick={() => setShowDrillDown(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowDrillDown(true);
            }
          }}
          sx={{
            fontSize: '17px',
            fontWeight: '700',
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: strategyNameColor,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
              textDecoration: 'underline',
            },
            '&:focus-visible': { outline: '2px solid #228B22', outlineOffset: 2, borderRadius: '2px' },
            transition: 'all 0.2s',
          }}
        >
          {strategy.strategy_type_name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {/* Merger Arbitrage only: measure utilisation against allocated risk capital or firmwide AUM. */}
          {isMergerArb && (
            <ToggleButtonGroup
              value={displayInAum}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) setDisplayInAum(newValue);
              }}
              size="small"
              aria-label="Utilisation basis"
              sx={{ height: 26 }}
            >
              <ToggleButton value={false} aria-label="display in rc" title="Allocated Risk Capital" sx={{ px: 1, textTransform: 'none' }}>
                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600 }}>RC</Typography>
              </ToggleButton>
              <ToggleButton value={true} aria-label="display in aum" title="Firmwide AUM" sx={{ px: 1, textTransform: 'none' }}>
                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600 }}>AUM</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {onSelectTrend && (
            <Tooltip title="Show this strategy in Risk Trends" arrow>
              <IconButton size="small" aria-label="show in risk trends" onClick={() => onSelectTrend(strategy.strategy_type_name)}>
                <ShowChartIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Risk Capital Utilization - All Exposure Measures */}
      <Box
        sx={{
          mb: 1.5,
          p: 1.5,
          backgroundColor: utilizationTint.bg,
          border: `1px solid ${utilizationTint.border}`,
          borderRadius: '4px',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
            <Typography
              variant="caption"
              sx={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px' }}
            >
              Risk Capital Utilization
            </Typography>
            <Box
              component="span"
              sx={{ px: 0.9, py: 0.15, borderRadius: '999px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap', color: utilizationStatus.color, backgroundColor: utilizationStatus.bg }}
            >
              {utilizationStatus.label}
            </Box>
          </Box>
        </Box>
        {/* Hero: the driving measure in dollars, its utilisation against the limit, and the headroom */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 1.25 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              {drivingLabel}
            </Typography>
            <Typography sx={{ fontSize: compact ? '22px' : '28px', fontWeight: 700, lineHeight: 1.1, color: drivingColor }}>
              {getDisplayValue(currentRatio.rawValue, drivingLabel)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ fontSize: compact ? '18px' : '22px', fontWeight: 700, lineHeight: 1.1, color: getUtilizationColor() }}>
              {utilizationPercent.toFixed(1)}%
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
              of {displayInAum ? 'AUM' : 'RC'} · max {currentRatio.max.toFixed(0)}%
            </Typography>
          </Box>
        </Box>
        {/* Plain div bar (not MUI LinearProgress): the theme's primary green kept overriding the fill colour. */}
        <Box
          role="progressbar"
          aria-valuenow={Math.round(Math.min(utilizationPercent, 100))}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${drivingLabel} utilisation ${utilizationPercent.toFixed(1)} percent, ${utilizationStatus.label}`}
          sx={{ height: '8px', borderRadius: '4px', backgroundColor: utilizationBar.track, overflow: 'hidden' }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${Math.min(Math.max(utilizationPercent, 0), 100)}%`,
              background: utilizationBar.fill,
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
            {displayInAum ? 'Firmwide AUM' : 'Allocated Risk Capital'}{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>
              {getDisplayValue(displayInAum ? aum : strategy.risk_capital, displayInAum ? 'Firmwide AUM' : 'Risk Capital')}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: getUtilizationColor() }}>{headroomText}</Typography>
        </Box>
      </Box>

      {/* All Exposures - Always Displayed */}
      <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: '700',
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            mb: 1,
            display: 'block',
          }}
        >
          All Exposures
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '12px', color: labelColor('gmv'), fontWeight: '600', textTransform: 'uppercase' }}>
              GMV
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', mb: 0.5 }}>
              {strategy.gmv === 0 ? '-' : getDisplayValue(strategy.gmv, 'GMV')}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: compact ? 'none' : 'block' }}>
              {strategy.gmv === 0 ? '-' : (displayInAum && aum ? `${((strategy.gmv / aum) * 100).toFixed(1)}%` : strategy.risk_capital ? `${((strategy.gmv / strategy.risk_capital) * 100).toFixed(1)}%` : '-')}
            </Typography>
            {renderTemperatureLine('gmv')}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '12px', color: labelColor('lmv'), fontWeight: '600', textTransform: 'uppercase' }}>
              LMV
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: '700', color: '#006842', mb: 0.5 }}>
              {strategy.lmv === 0 ? '-' : getDisplayValue(strategy.lmv, 'LMV')}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: compact ? 'none' : 'block' }}>
              {strategy.lmv === 0 ? '-' : (displayInAum && aum ? `${((strategy.lmv / aum) * 100).toFixed(1)}%` : strategy.risk_capital ? `${((strategy.lmv / strategy.risk_capital) * 100).toFixed(1)}%` : '-')}
            </Typography>
            {renderTemperatureLine('lmv')}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '12px', color: labelColor('smv'), fontWeight: '600', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              SMV
              {strategy.strategy_type_name === 'CONVERTIBLE ARBITRAGE' && (
                <Tooltip title="Excludes Credit Hedge and Rate Hedge exposures" arrow>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
                    <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#94a3b8' }} />
                  </Box>
                </Tooltip>
              )}
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: '700', color: '#B85450', mb: 0.5 }}>
              {strategy.smv === 0 ? '-' : getDisplayValue(strategy.smv, 'SMV')}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: compact ? 'none' : 'block' }}>
              {strategy.smv === 0 ? '-' : (displayInAum && aum ? `${((strategy.smv / aum) * 100).toFixed(1)}%` : strategy.risk_capital ? `${((strategy.smv / strategy.risk_capital) * 100).toFixed(1)}%` : '-')}
            </Typography>
            {renderTemperatureLine('smv')}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '12px', color: labelColor('nmv'), fontWeight: '600', textTransform: 'uppercase' }}>
              NMV
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', mb: 0.5 }}>
              {strategy.nmv === 0 ? '-' : getDisplayValue(strategy.nmv, 'NMV')}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: compact ? 'none' : 'block' }}>
              {strategy.nmv === 0 ? '-' : (displayInAum && aum ? `${((Math.abs(strategy.nmv) / aum) * 100).toFixed(1)}%` : strategy.risk_capital ? `${((Math.abs(strategy.nmv) / strategy.risk_capital) * 100).toFixed(1)}%` : '-')}
            </Typography>
            {renderTemperatureLine('nmv')}
          </Box>
        </Box>
      </Box>

      {/* Risk Metrics */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 1.5,
          pb: 1.5,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        {[
          { label: 'VaR 95%', value: strategy.var95, threshold: strategy.var95Threshold, thresholdPct: strategy.var95Pct },
          { label: 'VaR 99%', value: strategy.var99, threshold: strategy.var99Threshold, thresholdPct: strategy.var99Pct },
        ].map(({ label, value, threshold, thresholdPct }) => {
          const usedPct = varLimitUsedPct(value, threshold);
          const style = VAR_STATUS_STYLE[varLimitStatus(usedPct)];
          return (
            <Box
              key={label}
              sx={{
                flex: 1,
                minWidth: '100px',
                ...(style && { backgroundColor: style.bg, border: `1px solid ${style.border}`, borderRadius: '4px', p: 1 }),
              }}
            >
              <Typography
                variant="caption"
                sx={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', mb: 0 }}
              >
                {label}
              </Typography>
              <Typography sx={{ fontSize: '16px', fontWeight: '600', color: '#B85450' }}>
                {getDisplayValue(value, label)}
              </Typography>
              {threshold != null && (
                <Typography sx={{ fontSize: '11px', color: '#64748b', mt: 0.25 }}>
                  Limit {getDisplayValue(threshold, `${label} limit`)}
                  {thresholdPct != null && ` · ${Number(thresholdPct)}% of RC`}
                </Typography>
              )}
              {usedPct != null && (
                <Typography sx={{ fontSize: '12px', fontWeight: '700', color: style.color }}>
                  {usedPct.toFixed(0)}% of limit
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Portfolio Greeks. Other cards keep this row (so the grid rows stay aligned); SPAC has no
          Greeks, so it skips the row and its tables take the Greeks + tables rows instead of leaving a gap. */}
      {!isSpac && (
      <Box>
      {hasStrategyGreeks && (
        <Box
          sx={{
            mb: 1.5,
            pb: 1.5,
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <Typography
          sx={{
            fontSize: '12px',
            fontWeight: '700',
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            mb: 1.5,
            display: 'block',
          }}
        >
            Portfolio Greeks
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(strategyGreeks.length, 4)}, 1fr)`, gap: 1 }}>
            {strategyGreeks.map((scenario) => (
              <Box key={scenario.sim_number}>
                <Typography variant="caption" sx={{ fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>
                  {scenario.scenario || `Scenario ${scenario.sim_number}`}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', mb: 0.5 }}>
                  {scenario.pnl != null ? (
                    <ValueWithTooltip
                      value={scenario.pnl}
                      label={scenario.scenario || `Scenario ${scenario.sim_number}`}
                      kUnderHundredK
                    />
                  ) : '-'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      </Box>
      )}

      {/* Strategy-Specific Collapsible Tables */}
      <Box sx={isSpac ? { gridRow: 'span 2' } : undefined}>
      {strategy.strategy_type_name === 'MERGER ARBITRAGE' && (
        <>
          <CollapsibleTable title="Top 5 Strategies by Size" data={strategy.top5StrategiesBySize} aum={aum} />
          <CollapsibleTable title="Top 5 Deal Break Risk" data={strategy.top5DealBreakRisk} />
        </>
      )}

      {strategy.strategy_type_name === 'SPAC LIQUIDATION ARBITRAGE' && (
        <>
          <CollapsibleTable title="SPAC Common Risk" data={strategy.top5SpacCommon} riskLabel="Risk to 100 Bps Spread" limit={Infinity} />
          <CollapsibleTable title="SPAC Warrant Risk" data={strategy.top5SpacWarrant} riskLabel="Risk to 75% Drop in Warrants" limit={Infinity} />
          <CollapsibleTable title="SPAC Right Risk" data={strategy.top5SpacRight} riskLabel="Risk to 75% Drop in Rights" limit={Infinity} />
        </>
      )}

      {[
        'EDFI',
        'CONVERTIBLE ARBITRAGE',
        'CREDIT ARBITRAGE',
        'VOL ARBITRAGE',
        'STAT ARBITRAGE',
      ].includes(strategy.strategy_type_name) && (
        <CollapsibleTable title="Top 5 Strategies by Size" data={strategy.top5StrategiesBySize} aum={aum} />
      )}
      </Box>

      {/* Drill-down Modal */}
      <StrategyTypeDrillDownModal
        open={showDrillDown}
        onClose={() => setShowDrillDown(false)}
        strategyTypeName={strategy.strategy_type_name}
        asOfDate={asOfDate}
        riskCapital={strategy.risk_capital}
        drivingMeasure={drivingMeasure}
        limitCaps={{
          max_gmv_pct: strategy.max_gmv_pct,
          max_lmv_pct: strategy.max_lmv_pct,
          max_smv_pct: strategy.max_smv_pct,
          max_nmv_pct: strategy.max_nmv_pct,
          max_wt_rt_lmv_pct: strategy.max_wt_rt_lmv_pct,
        }}
      />
    </Card>
  );
}
